import classNames from 'classnames';
import type { Column } from 'shared/ui/Table';
import { ClippedText } from 'shared/ui/ClippedText';
import { BudgetInput } from 'shared/ui/Input';
import { formatPrice } from 'shared/lib/formatting';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { centsToDollars } from 'shared/lib/format';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { Tooltip } from 'shared/ui/Tooltip';

import type { CampaignTypeKey } from '../../@x';

import type { BidRangeProps, BidRangeTableColumn } from './index';

type getTableDataParams = Pick<
  BidRangeProps,
  | 'bidParams'
  | 'bidTierGroups'
  | 'campaignType'
  | 'isLessThanMinimumTooltip'
  | 'isOneOfFieldsLessThanMinimum'
  | 'bidValue'
  | 'bidTiersValues'
>;

export function getTableData({
  bidParams,
  bidTierGroups,
  campaignType,
  isLessThanMinimumTooltip,
  isOneOfFieldsLessThanMinimum,
  bidValue,
  bidTiersValues,
}: getTableDataParams): BidRangeTableColumn[] {
  const bidTiers = bidTierGroups?.[0]?.bidTiers ?? [];

  if (bidParams) {
    const tableData: BidRangeTableColumn[] = [
      {
        index: 0,
        bidRange: getFormattedBidRange({ campaignType, ...bidParams }),
      },
    ];

    if (isLessThanMinimumTooltip) {
      tableData[0].isOneOfFieldsLessThanMinimum = isOneOfFieldsLessThanMinimum;
    }

    if (
      isLessThanMinimumTooltip &&
      bidValue &&
      checkIsLessThanMinimumBid(getMinBidDollars({ campaignType, ...bidParams }), bidValue)
    ) {
      tableData[0].tooltipText = getLessThanMinimumTooltipText(getMinBidDollars({ campaignType, ...bidParams }));
    }

    return tableData;
  }

  return bidTiers.map((bidTier, index) => {
    const tableData: BidRangeTableColumn = {
      index,
      bidTierLabel: bidTier.label,
      bidRange: getFormattedBidRange({
        campaignType,
        ...bidTier,
      }),
    };

    if (isLessThanMinimumTooltip) {
      tableData.isOneOfFieldsLessThanMinimum = isOneOfFieldsLessThanMinimum;
    }

    if (
      isLessThanMinimumTooltip &&
      bidTiersValues &&
      bidTiersValues[index] &&
      bidTiersValues[index].bid &&
      checkIsLessThanMinimumBid(getMinBidDollars({ campaignType, ...bidTier }), bidTiersValues[index].bid)
    ) {
      tableData.tooltipText = getLessThanMinimumTooltipText(getMinBidDollars({ campaignType, ...bidTier }));
    }

    return tableData;
  });
}

export function getMinBidDollars(params: {
  campaignType: CampaignTypeKey;
  leadMinimalBid: number;
  apptMinimalBid: number;
}) {
  const isApptCampaign = params.campaignType === 'appt';

  return centsToDollars(isApptCampaign ? params.apptMinimalBid : params.leadMinimalBid);
}

export function checkIsLessThanMinimumBid(minBid: number, currentBid: string) {
  return minBid > Number(currentBid.replace(/[$,]/g, '') || 0);
}

function getLessThanMinimumTooltipText(minBid: number) {
  return `In this service minimal bid is $${minBid}.
  Consider increasing it to win more auctions and receive more
  quality leads`;
}

function getBudgetInputTestAttribute(index: number, isBidTierExist: boolean) {
  return (
    isBidTierExist
      ? getDataAutoTestAttributes([`bid-input-${index + 1}`])
      : getDataAutoTestAttributes(['bid-input-single'])
  ).attributes;
}

function getFormattedBidRange(params: {
  campaignType: CampaignTypeKey;
  apptMaximumBid: number;
  leadMaximumBid: number;
  leadMinimalBid: number;
  apptMinimalBid: number;
}) {
  const isApptCampaign = params.campaignType === 'appt';
  const minBid = isApptCampaign ? params.apptMinimalBid : params.leadMinimalBid;
  const maxBid = isApptCampaign ? params.apptMaximumBid : params.leadMaximumBid;

  if (params.campaignType === 'sales') {
    return `${formatPrice(minBid)}`;
  }

  return `${formatPrice(minBid)} - ${formatPrice(maxBid)}`;
}

export function getColumns({
  bidTierColumnTitle = '',
  isWideScreen,
  isDisabled,
  isFocused,
  isOneOfFieldsLessThanMinimum,
  setRate,
}: {
  bidTierColumnTitle?: string;
  isWideScreen?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  isOneOfFieldsLessThanMinimum?: boolean;
  setRate?: number;
}) {
  const columns: Column<BidRangeTableColumn>[] = [];
  const isBidTierExist = !!bidTierColumnTitle;

  if (isBidTierExist) {
    columns.push({
      label: bidTierColumnTitle,
      key: 'bidTierGroup',
      columnCell: ({ row }) => (
        <td
          className={classNames({
            'w-full': isWideScreen,
            'break-words': !isWideScreen,
            'max-w-[76px]': isOneOfFieldsLessThanMinimum,
            'max-w-[100px] !pr-2': !isOneOfFieldsLessThanMinimum,
          })}
        >
          <ClippedText
            clamp={2}
            text={row.bidTierLabel}
            tooltip={{ content: row.bidTierLabel }}
          />
        </td>
      ),
      headerCell: () => (
        <th
          className={classNames('text-[11px]', {
            'w-full': isWideScreen,
            'break-words': !isWideScreen,
            'max-w-[76px]': isOneOfFieldsLessThanMinimum,
            'max-w-[115px] min-w-[115px]': !isOneOfFieldsLessThanMinimum,
          })}
        >
          <ClippedText
            clamp={1}
            text={bidTierColumnTitle}
            tooltip={{ content: bidTierColumnTitle }}
            isForceTruncated={isOneOfFieldsLessThanMinimum}
          />
        </th>
      ),
    });
  }

  columns.push(
    {
      label: 'Bid range',
      key: 'bidRange',
      columnCell: ({ row }) => (
        <td
          className={classNames('whitespace-nowrap font-medium', {
            'text-center': isBidTierExist && !isWideScreen && !setRate,
            'text-left': isBidTierExist && isWideScreen,
          })}
          {...getDataAutoTestAttributes([`bid-range-value`]).attributes}
        >
          {row.bidRange}
        </td>
      ),
      headerCell: (props) => (
        <th
          className={classNames('text-[11px] flex', {
            'text-center flex-col w-[124px] min-w-[124px]': isBidTierExist && !isWideScreen,
            'text-left': isBidTierExist && isWideScreen,
            'w-[206px] min-w-[206px]': isWideScreen,
            'items-end': setRate && isWideScreen,
            'items-start leading-[14px]': isBidTierExist && setRate && !isWideScreen,
            'items-center leading-[14px]': !isBidTierExist && setRate && !isWideScreen,
          })}
        >
          {props.value}
          {setRate ? (
            <>
              <div
                className={classNames('flex items-center', {
                  'ml-1': !(isBidTierExist && setRate && !isWideScreen),
                })}
              >
                <div className={classNames('whitespace-nowrap', isDisabled ? 'text-slate-400' : 'text-slate-900')}>
                  (SET RATE {setRate}%)
                </div>
                <Tooltip
                  placement={isBidTierExist && setRate && !isWideScreen ? 'top' : 'top-start'}
                  content={
                    <div className="text-sm text-slate-700">
                      Set rate is the agreed-upon percentage you approved. It acts as a multiplier in the total cost.
                    </div>
                  }
                  className={classNames('flex self-center ml-[2px] hover:text-deep-blue-500 cursor-pointer')}
                  widthClass="max-w-[260px]"
                  contentClass="!text-left text-slate-400"
                >
                  <MaterialIcon
                    className="text-[16px]"
                    {...getDataAutoTestAttributes([`set-rate-tooltip-icon`])}
                  >
                    info
                  </MaterialIcon>
                </Tooltip>
              </div>
            </>
          ) : null}
        </th>
      ),
    },
    {
      label: 'Your bid',
      key: 'yourBid',
      columnCell: ({ row: { index }, row }) => {
        const inputName = isBidTierExist ? `bidTiers.${index}.bid` : 'bid';
        const isFirstBidFocused = isFocused && index === 0;

        return (
          <td className={classNames('text-right', { '!pr-0': !isWideScreen })}>
            <BudgetInput
              attributes={{
                maxLength: 9,
                ...getBudgetInputTestAttribute(index, isBidTierExist),
              }}
              isFocused={isFirstBidFocused}
              isHiddenError
              disabled={isDisabled}
              placeholder=""
              className={classNames('input-sm inline-block', {
                'w-[104px]': row.isOneOfFieldsLessThanMinimum,
                'w-[80px]': !row.isOneOfFieldsLessThanMinimum,
                'pr-2': !isWideScreen,
              })}
              inputClassName={classNames({
                'text-left': isWideScreen,
                'text-right': !isWideScreen,
              })}
              name={inputName}
              thousandSeparator
              tooltipText={row.tooltipText}
            />
          </td>
        );
      },
      headerCell: ({ value }) => (
        <th
          className={classNames('text-[11px] text-right pr-2', {
            'whitespace-nowrap !px-11': isWideScreen,
          })}
        >
          {value}
        </th>
      ),
    }
  );

  return columns;
}
